import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {CoreConfigService} from '../../../../@core/services/config.service';
import {ProfileService} from '../../../core/services/profile.service';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LocalStorageService} from '../../../core/services/local-storage.service';
import {AlertService} from '../../../core/services/alert.service';
import {TranslateService} from '@ngx-translate/core';
import {CourseFilterService} from '../../../core/services/course-filter.service';
import {LoginService} from '../../../core/services/login.service';

@Component({
  selector: 'app-citizen-profile',
  templateUrl: './citizen-profile.component.html',
  styleUrls: ['./citizen-profile.component.scss']
})
export class CitizenProfileComponent implements OnInit, OnDestroy {

  private readonly _unsubscribeAll: Subject<any>;

  submitted: boolean = false;
  coreConfig: any;
  userId: number;
  organizations: string[] = ['Onderwijsinstelling', 'Pedagogische begeleidingsdienst', 'Leersteuncentrum', 'Bibliotheek',
    'Onderwijsbeleid', 'Hulpverleningsdienst', 'Privé-praktijk/Groepspraktijk', 'Particulier (geen organisatie)'];
  functions: string[] = ['Leerkracht', 'Directie', 'Interne zorg in school', 'Leerondersteuner', 'Pedagogisch begeleider',
    'Hulpverlener', 'Beleidsmaker', 'Onderzoeker', 'Student', 'Geen functie'];
  contactMissing: boolean = false;
  privatePurchaseCheck: boolean = true;

  private readonly defaultAlertOptions = {
    closeButton: true,
    autoClose: true,
    keepAfterRouteChange: false
  };

  constructor(private readonly _coreConfigService: CoreConfigService,
              private readonly _formBuilder: UntypedFormBuilder,
              private readonly profileService: ProfileService,
              private readonly _localStorageService: LocalStorageService,
              private readonly _router: Router,
              private readonly alertService: AlertService,
              private readonly translate: TranslateService,
              public courseFilterService: CourseFilterService,
              private readonly loginService: LoginService) {

    this._coreConfigService.setConfig({
      layout: {
        navbar: {
          hidden: true
        },
        menu: {
          hidden: true
        },
        footer: {
          hidden: true
        },
        customizer: false,
        enableLocalStorage: false
      }
    });
    this._unsubscribeAll = new Subject();
  }

  public accountForm: UntypedFormGroup = this._formBuilder.group({
    inss: ['', Validators.required],
    firstName: ['', Validators.required],
    name: ['', Validators.required],
    phoneNumber: [''],
    billingAddress: this._formBuilder.group({
      organizationName: [''],
      postcode: ['', Validators.required],
      city: ['', Validators.required],
      street: ['', Validators.required],
      number: ['', Validators.required],
    }),
    accountEmail: ['', [Validators.required, Validators.email]],
    isShippingSameAsBilling: [true],
    subscribeToEmag: [false],
    shippingAddress: this._formBuilder.group({
      organizationName: ['',],
      postcode: [''],
      city: [''],
      street: [''],
      number: [''],
    }),
    organiser: ['', Validators.required],
    function: ['', Validators.required],
    privacyStatement: [false, Validators.requiredTrue],
    termsStatement: [false, Validators.requiredTrue],
    contactByEmail: [false],
    contactBySms: [false]
  });

  ngOnInit(): void {
   this.profileService.fetchDataForUnverifiedUser().subscribe(unverifiedUser => {
      this.accountForm.patchValue({
        inss: unverifiedUser.inss,
        firstName: unverifiedUser.firstName,
        name: unverifiedUser.name
      });
    });


    this.accountForm.get('isShippingSameAsBilling')?.valueChanges.subscribe(isShippingAsBilling => {
      ['postcode', 'city', 'street', 'number','organizationName']
        .forEach(controlName => {
          const control = this.accountForm.get('shippingAddress').get(controlName);
          control.setValidators(isShippingAsBilling ? [] : Validators.required);
          control.updateValueAndValidity();
        });
    });

    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
      this.coreConfig = config;
    });
    this.courseFilterService.getFilterConstants();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  saveAccount(): void {
    this.submitted = true;
    this.contactMissing = !this.hasAnyContactPreference();

    if (this.accountForm.valid && !this.contactMissing) {
      const smsReset = this.validateContactPreferences();
      this.profileService.citizenRegister(this.accountForm.value)
        .subscribe(userId => {
          this.profileService.userIdForCitizen.next(userId);
          if (smsReset) {
            this.alertService.warn(this.translate.instant('CONTACT_PREFERENCE_ALERT'), this.defaultAlertOptions);
          } else {
            this.alertService.success(this.translate.instant('USER_INFO_SUCCESS_CHANGE'), this.defaultAlertOptions);
          }
          this._localStorageService.clearUserData();
          this.loginService.sendDeleteTokensAndOpenLogin();
          this._router.navigateByUrl('/verify-mail');
        });
    }
  }

  setFunctions(functionItem: string) {
    this.accountForm.get('function').setValue(functionItem);
  }

  setOrganiser(value: string) {
    this.accountForm.get('organiser').setValue(value);
  }

  onContactPreferenceChange(event: any): void {
    const val = event.target.value;
    if (val === 'SMS') {
      this.accountForm.patchValue({
        contactBySms: !this.userPhoneNumberMissing() && event.target.checked
      });
    } else if (val === 'EMAIL') {
      this.accountForm.patchValue({
        contactByEmail: event.target.checked
      });
    }
    this.contactMissing = !this.hasAnyContactPreference();
  }

  onPrivatePurchaseChange(event: any, choice: string): void {
    if (choice === 'yes') {
      this.privatePurchaseCheck = event.target.checked;
    } else {
      this.privatePurchaseCheck = !event.target.checked;
    }
    if (this.privatePurchaseCheck) {
      this.accountForm.get('billingAddress').get('organizationName').setValue('');
    }
    this.accountForm.get('billingAddress').get('organizationName').setValidators(this.privatePurchaseCheck ? [] : Validators.required);
    this.accountForm.get('billingAddress').get('organizationName').updateValueAndValidity();
  }

  validateSMSOptionCheck() {
    return this.accountForm.value.contactBySms && !this.userPhoneNumberMissing();
  }

  validateContactPreferences() {
    if (this.accountForm.value.contactBySms && this.userPhoneNumberMissing()) {
      this.accountForm.patchValue({ contactBySms: false });
      return true;
    }
    return false;
  }

  userPhoneNumberMissing() {
    return this.accountForm.value.phoneNumber === '' || !this.accountForm.value.phoneNumber;
  }

  hasAnyContactPreference(): boolean {
    return !!this.accountForm.value.contactByEmail || !!this.accountForm.value.contactBySms;
  }
}
